import { useMemo } from "react";
import { Box, IconButton, Stack } from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from "@mui/x-data-grid";
import { ConfirmationDialog, CustomDataGrid } from "@shared/components";
import { useSortModel } from "@shared/components/grid-components/hooks/useSortModel";
import { TableActions } from "@shared/utils/constants";
import { formatDateTime, getActionPermission } from "@shared/utils/functions";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GridFilterModel } from "@mui/x-data-grid";
import { useGetAllPartners } from "../services/useGetAllPartners";
import { AllowedSortFields, programOptions } from "../utils/constants";
import { useGetAllPublishers } from "@modules/publishers/services/useGetAllPublishers";
import { useGetAllInvoiceParties } from "@modules/invoice-parties/services/useGetInvoiceParties";
import { useGetAllPartnerChannels } from "@modules/partner-channel/services/useGetAllPartnerChannels";
import {
  IPartnerRow,
  PartnersFormValues,
  PartnersProps,
  SortField,
} from "../utils/types";
import { useDeletePartner } from "../services/useDeletePartner";
import PartnerForm from "./PartnerForm";
import { useUpdatePartner } from "../services/useUpdatePartner";
import { FormikHelpers } from "formik";
import { enqueueSnackbar } from "notistack";
import { FormActions } from "@shared/utils/constant";
import { Can } from "@core/casl-provider";
import { useCreateNewPartner } from "../services/useCreateNewPartner";
import { ContentCopy } from "@mui/icons-material";

const CampaignTableActions = [
  {
    key: "DUPLICATE",
    label: "Duplicate",
    icon: (
      <ContentCopy
        fontSize="small"
        sx={(theme) => ({
          color:
            theme.palette.mode === "dark"
              ? theme.palette.common.white
              : theme.palette.grey[500],
        })}
      />
    ),
  },
  ...TableActions,
] as const;

const PartnersDataGrid = ({
  visibleColumns,
  setPaginationModel,
  paginationModel,
}: PartnersProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const referrerID = searchParams.get("referrerID") ?? "";
  const filterField = searchParams.get("filterField") ?? "";
  const filterOperator = searchParams.get("filterOperator") ?? "";
  const filterValue = searchParams.get("filterValue") ?? "";
  const userName = searchParams.get("username") ?? "";
  const updatedSince = searchParams.get("updatedSince") ?? "";
  const titleID = searchParams.get("titleID") ?? "";
  const partnerChannel = searchParams.get("partnerChannel") ?? "";
  const platform = searchParams.get("platform") ?? "";
  const invoiceParty = searchParams.get("invoiceParty") ?? "";

  const { sortModel, handleSortModelChange, orderBy, order } =
    useSortModel<SortField>({
      allowedSortFields: AllowedSortFields,
    });

  const { data: partnersList, isLoading } = useGetAllPartners({
    data: {
      pageNumber: String(paginationModel.page + 1),
      pageSize: String(paginationModel.pageSize),
      includeInactive: true,
      referrerID: referrerID,
      filterField: filterField,
      filterOperator: filterOperator,
      filterValue: filterValue,
      username: userName,
      titleID: titleID,
      partnerChannel: partnerChannel,
      platform: platform,
      invoiceParty: invoiceParty,
      orderBy: orderBy,
      order: order,
      updatedSince: updatedSince,
    },
  });

  // The column filter is applied by the server (so that pagination and the
  // total count stay correct). The full model (also while a value is still
  // being entered) is kept in state; only the active filter goes to the URL.
  const [filterModel, setFilterModel] = useState<GridFilterModel>(() => ({
    items: filterField
      ? [
          {
            id: "column-filter",
            field: filterField,
            operator: filterOperator,
            value:
              filterOperator === "isAnyOf"
                ? filterValue.split(",").filter(Boolean)
                : filterValue,
          },
        ]
      : [],
  }));

  const handleFilterModelChange = (model: GridFilterModel) => {
    setFilterModel(model);
    const item = model.items[0];
    const field = item?.field ?? "";
    const operator = item?.operator ?? "";
    const rawValue: unknown = item?.value;
    const value = (
      Array.isArray(rawValue) ? rawValue.join(",") : String(rawValue ?? "")
    ).trim();
    // Filters without a value (other than isEmpty / isNotEmpty) don't filter
    const isActive =
      !!item && (value !== "" || ["isEmpty", "isNotEmpty"].includes(operator));

    const nextField = isActive ? field : "";
    const nextOperator = isActive ? operator : "";
    const nextValue = isActive ? value : "";
    if (
      nextField === filterField &&
      nextOperator === filterOperator &&
      nextValue === filterValue
    )
      return;

    const next = new URLSearchParams(searchParams);
    next.delete("filterField");
    next.delete("filterOperator");
    next.delete("filterValue");
    if (isActive) {
      next.set("filterField", nextField);
      next.set("filterOperator", nextOperator);
      if (nextValue) next.set("filterValue", nextValue);
    }
    // Results change, so go back to the first page
    next.delete("page");
    setSearchParams(next, { replace: true });
  };

  const { mutate: deleteMutation } = useDeletePartner();
  const { mutateAsync: updateFormMutationAsync, isPending: isFormPending } =
    useUpdatePartner();

  const { mutateAsync: createPartnerAsync, isPending: isCreatePending } =
    useCreateNewPartner();

  const [actionState, setActionState] = useState<{
    action: "EDIT" | "DELETE" | "DUPLICATE" | "";
    payload: Partial<PartnersFormValues>;
  }>({
    action: "",
    payload: {},
  });

  const [allConfigurations, setAllConfigurations] = useState<IPartnerRow[]>();
  const clearActionState = () => setActionState({ action: "", payload: {} });

  // Fetch publishers and invoice parties for name lookups
  const { data: publishersResp } = useGetAllPublishers({
    data: { pageNumber: "1", pageSize: "1000" },
  });

  const { data: invoiceResp } = useGetAllInvoiceParties({
    data: { pageNumber: "1", pageSize: "1000", includeInactive: true },
  });

  const { data: partnerChannelResp } = useGetAllPartnerChannels({
    data: { pageNumber: "1", pageSize: "1000", includeInactive: true },
  });

  // Create lookup objects for IDs to names
  const publisherNameLookup = useMemo(() => {
    const lookup: Record<number, string> = {};
    publishersResp?.data?.forEach((publisher) => {
      lookup[publisher.id] = publisher.name || `Publisher ${publisher.id}`;
    });
    return lookup;
  }, [publishersResp]);

  const invoicePartyNameLookup = useMemo(() => {
    const lookup: Record<string, string> = {};

    invoiceResp?.data?.forEach((invoiceParty) => {
      const id = String(invoiceParty.uid);
      lookup[id] = invoiceParty.name || `Invoice Party ${id}`;
    });
    return lookup;
  }, [invoiceResp]);

  const partnerChannelNameLookup = useMemo(() => {
    const lookup: Record<string, string> = {};

    partnerChannelResp?.data?.forEach((partnerChannel) => {
      const id = String(partnerChannel.channelId);
      lookup[id] = partnerChannel.channelName || `Partner Channel ${id}`;
    });
    return lookup;
  }, [partnerChannelResp]);

  const partnerProgramLookup = useMemo(() => {
    const lookup: Record<string, string> = {};
    programOptions.forEach((option) => {
      lookup[option.value] = option.label;
    });
    return lookup;
  }, []);

  const totalRows: number =
    (partnersList?.pagination?.total as number) ??
    allConfigurations?.length ??
    0;
  const columns: GridColDef<IPartnerRow>[] = [
    {
      field: "referrerID",
      headerName: "Referrer ID",
      flex: 0.2,
      minWidth: 120,
      sortable: false,
    },
    {
      field: "username",
      headerName: "Campaign Name",
      flex: 1,
      minWidth: 160,
      sortable: true,
      renderCell: (params: GridRenderCellParams<IPartnerRow, string>) => (
        <span style={{ textTransform: "capitalize" }}>{params.value}</span>
      ),
    },
    {
      field: "password",
      headerName: "Password",
      flex: 1,
      minWidth: 140,
      sortable: true,
    },
    {
      field: "partnerProgram",
      headerName: "Partner Program",
      flex: 1,
      minWidth: 180,
      sortable: true,
      renderCell: (
        params: GridRenderCellParams<IPartnerRow, string | number>,
      ) => {
        const programValue = String(params.value || "");
        return partnerProgramLookup[programValue] || `Program ${programValue}`;
      },
    },
    {
      field: "partnerChannel",
      headerName: "Partner Channel",
      flex: 1,
      minWidth: 180,
      sortable: true,
      renderCell: (
        params: GridRenderCellParams<IPartnerRow, string | null>,
      ) => {
        const partnerChannelId = params.value;
        if (!partnerChannelId) return "";
        return (
          partnerChannelNameLookup[partnerChannelId] ||
          `Partner Channel ${partnerChannelId}`
        );
      },
    },
    {
      field: "partnerTitleKeys",
      headerName: "Partner Title Keys",
      flex: 1,
      minWidth: 180,
      sortable: true,
    },
    {
      field: "disableRewardedAdsForDays",
      headerName: "Disable Rewarded Ads For Days",
      flex: 1,
      minWidth: 220,
      sortable: true,
    },
    {
      field: "platform",
      headerName: "Platform",
      flex: 1,
      minWidth: 140,
      sortable: true,
    },
    {
      field: "device",
      headerName: "Device",
      flex: 1,
      minWidth: 140,
      sortable: true,
    },
    {
      field: "invoiceParty",
      headerName: "Invoice Party",
      flex: 1,
      minWidth: 160,
      sortable: true,
      renderCell: (
        params: GridRenderCellParams<IPartnerRow, number | null>,
      ) => {
        const invoicePartyId = params.value;
        if (invoicePartyId === null || invoicePartyId === undefined) return "";
        return (
          invoicePartyNameLookup[invoicePartyId] ||
          `Invoice Party ${invoicePartyId}`
        );
      },
    },
    {
      field: "publisherID",
      headerName: "Publisher",
      flex: 1,
      minWidth: 160,
      sortable: true,
      renderCell: (params: GridRenderCellParams<IPartnerRow, number>) => {
        const publisherId = params.value;
        return publisherNameLookup[publisherId] || publisherId;
      },
    },

    {
      field: "createdAt",
      headerName: "Created At",
      flex: 1,
      minWidth: 210,
      sortable: true,
      renderCell: (params: GridRenderCellParams<IPartnerRow, string>) =>
        formatDateTime(params.value),
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      flex: 1,
      minWidth: 210,
      sortable: true,
      renderCell: (params: GridRenderCellParams<IPartnerRow, string>) =>
        formatDateTime(params.value),
    },

    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      filterable: false,
      headerAlign: "center",
      renderCell: (
        params: GridRenderCellParams<
          IPartnerRow,
          unknown,
          unknown,
          GridTreeNodeWithRender
        >,
      ) => {
        return (
          <Stack
            direction="row"
            spacing={1}
            alignItems={"center"}
            sx={{ height: "100%" }}
          >
            {CampaignTableActions.map((action, index) => {
              const actionPermission = getActionPermission(action);
              return (
                <Can do={actionPermission} on="Campaigns" key={index}>
                  <IconButton
                    key={index}
                    title={action.label}
                    style={{
                      backgroundColor: "primary.main",
                    }}
                    onClick={() =>
                      setActionState({
                        action: action.key,
                        payload: {
                          referrerID: Number(params.row.referrerID),
                          username: params.row.username,
                          password: params.row.password,
                          partnerProgram: params.row.partnerProgram,
                          partnerChannel: params.row.partnerChannel,
                          partnerTitleKeys: params.row.partnerTitleKeys,
                          disableRewardedAdsForDays:
                            params.row.disableRewardedAdsForDays,
                          platform: params.row.platform,
                          device: params.row.device,
                          invoiceParty: params.row.invoiceParty,
                          publisherID: Number(params.row.publisherID),
                        },
                      })
                    }
                  >
                    {action.icon}
                  </IconButton>
                </Can>
              );
            })}
          </Stack>
        );
      },
    },
  ];

  const handleDeleteConfirm = async () => {
    deleteMutation({ referrerID: String(actionState.payload.referrerID) });
    clearActionState();
  };

  const handleEditSubmit = async (
    values: PartnersFormValues & { id?: string },
    helpers: FormikHelpers<PartnersFormValues & { id?: string }>,
  ) => {
    try {
      const referrerID = actionState.payload.referrerID;
      const payloadData = { ...values };

      await updateFormMutationAsync({
        payload: { referrerID: referrerID as number, payloadData },
      });
      clearActionState();
    } catch (error: unknown) {
      const errMsg: string =
        error instanceof Error ? error.message : "Failed to update partner";
      enqueueSnackbar(errMsg, {
        variant: "error",
        autoHideDuration: 5000,
        preventDuplicate: true,
      });
    } finally {
      helpers.setSubmitting(false);
    }
  };

  const handleDuplicateSubmit = async (
    values: PartnersFormValues & { id?: string },
    helpers: FormikHelpers<PartnersFormValues & { id?: string }>,
  ) => {
    try {
      const payload = {
        referrerID: values.referrerID,
        username: values.username,
        password: values.password,
        partnerProgram: values.partnerProgram,
        partnerChannel: values.partnerChannel,
        partnerTitleKeys: values.partnerTitleKeys,
        disableRewardedAdsForDays: values.disableRewardedAdsForDays,
        platform: values.platform,
        device: values.device,
        invoiceParty: values.invoiceParty,
        publisherID: values.publisherID,
      };

      await createPartnerAsync({ payload });
      clearActionState();
    } catch (error: unknown) {
      const errMsg: string =
        error instanceof Error ? error.message : "Failed to duplicate partner";
      console.error("errMsg:", errMsg);
    } finally {
      helpers.setSubmitting(false);
    }
  };

  useEffect(() => {
    if (partnersList?.data) {
      setAllConfigurations(partnersList.data);
    }
  }, [partnersList]);

  return (
    <Box
      sx={(theme) => ({
        border: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        borderBottomLeftRadius: theme.shape.borderRadius,
        borderBottomRightRadius: theme.shape.borderRadius,
        overflow: "hidden",
      })}
    >
      <CustomDataGrid<IPartnerRow>
        rows={allConfigurations}
        columns={columns}
        visibleColumns={visibleColumns}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        getRowId={(row) =>
          row.referrerID ??
          `${row.username}-${row.password}-${row.partnerProgram}-${
            row.updatedAt ?? ""
          }`
        }
        rowCount={totalRows}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        autoHeight
        disableSelectionOnClick
        loading={isLoading}
        dataGridSx={(theme) => ({
          "& .MuiDataGrid-columnHeaders": {
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
        })}
      />

      <ConfirmationDialog
        open={actionState.action === FormActions.DELETE}
        onClose={clearActionState}
        onConfirm={handleDeleteConfirm}
        title={`Delete ${actionState.payload.username ?? "Campaign Name"}`}
        message="Are you sure you want to Delete?"
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="primary"
      />
      <PartnerForm
        open={actionState.action === FormActions.EDIT}
        onClose={clearActionState}
        title="Edit Campaign"
        initialValues={actionState.payload}
        disable={isFormPending}
        onSubmit={handleEditSubmit}
        showCloseButton
      />

      <PartnerForm
        open={actionState.action === FormActions.DUPLICATE}
        onClose={clearActionState}
        title="Duplicate Campaign"
        initialValues={actionState.payload}
        disable={isCreatePending}
        onSubmit={handleDuplicateSubmit}
        showCloseButton
      />
    </Box>
  );
};

export default PartnersDataGrid;
